// ============================================================================
// SM-2 spaced-repetition scheduler + mastery-state machine (spec §8.1).
// ----------------------------------------------------------------------------
// Pure functions, no side effects. Given a prior per-item schedule and a grade,
// return the next schedule. This is the pedagogical heart of the app and the
// most-tested module. Reused unchanged by the web and mobile clients (§15.5).
//
// A "schedule" is one (learner, item, skill-direction) record:
//   { mastery_state, ease_factor, interval_days, repetitions,
//     learning_step, lapse_count, is_leech, is_suspended, due_at }
// Time is passed in explicitly (nowMs) so the function is deterministic/testable.
// ============================================================================

import { MASTERY, GRADE } from '../models/constants.js';

const DAY_MS = 24 * 60 * 60 * 1000;
const MIN_MS = 60 * 1000;

/** Tunable scheduler configuration. Leech threshold is confirmable (spec §20.2 #8). */
export const DEFAULT_CONFIG = Object.freeze({
  learningStepsMin: [1, 10],   // short in-session steps before graduation
  relearnStepsMin: [10],       // steps after a lapse
  graduatingIntervalDays: 1,   // first day-interval on "good" graduation
  easyIntervalDays: 4,         // first day-interval on "easy" graduation
  minEaseFactor: 1.3,
  startingEaseFactor: 2.5,
  easyBonus: 1.3,              // extra multiplier for "easy" in review
  hardMultiplier: 1.2,         // interval growth for "hard" in review
  masteredIntervalDays: 21,    // interval at/above which a record is "mastered"
  leechLapses: 8,              // lapses that flag an item as a leech
  suspendLeeches: false,       // auto-suspend chronic leeches (default off, §20.2)
  maxIntervalDays: 365 * 5,    // hard ceiling so intervals stay sane
});

/** A brand-new, never-studied schedule for an item. */
export function newSchedule(overrides = {}) {
  return {
    mastery_state: MASTERY.NEW,
    ease_factor: DEFAULT_CONFIG.startingEaseFactor,
    interval_days: 0,
    repetitions: 0,
    learning_step: 0,
    lapse_count: 0,
    is_leech: false,
    is_suspended: false,
    due_at: null,
    ...overrides,
  };
}

const clampEase = (ef, cfg) => Math.max(cfg.minEaseFactor, Number(ef.toFixed(4)));
const roundInterval = (days, cfg) =>
  Math.min(cfg.maxIntervalDays, Math.max(1, Math.round(days)));

/** True while the item is still in (re)learning steps rather than day-intervals. */
function isLearningPhase(state) {
  return (
    state === MASTERY.NEW ||
    state === MASTERY.LEARNING ||
    state === MASTERY.LAPSED
  );
}

/** State once an item is on day-intervals: mastered at/above the threshold. */
function reviewState(intervalDays, cfg) {
  return intervalDays >= cfg.masteredIntervalDays
    ? MASTERY.MASTERED
    : MASTERY.FAMILIAR;
}

/**
 * Advance a schedule by one graded review.
 * @param {object} prev  prior schedule (or newSchedule())
 * @param {'again'|'hard'|'good'|'easy'} grade
 * @param {object} [opts]
 * @param {number} [opts.nowMs]  current time in ms (defaults to Date.now())
 * @param {object} [opts.config] scheduler config overrides
 * @returns {object} the next schedule
 */
export function schedule(prev, grade, opts = {}) {
  const cfg = { ...DEFAULT_CONFIG, ...(opts.config || {}) };
  const nowMs = opts.nowMs ?? Date.now();
  const s = { ...newSchedule(), ...prev };

  if (!Object.values(GRADE).includes(grade)) {
    throw new Error(`schedule(): unknown grade "${grade}"`);
  }

  return isLearningPhase(s.mastery_state)
    ? stepLearning(s, grade, nowMs, cfg)
    : stepReview(s, grade, nowMs, cfg);
}

// --- (Re)learning phase: short minute-steps, interval_days stays 0 ----------
function stepLearning(s, grade, nowMs, cfg) {
  const relearning = s.mastery_state === MASTERY.LAPSED;
  const steps = relearning ? cfg.relearnStepsMin : cfg.learningStepsMin;
  const next = { ...s };

  if (grade === GRADE.EASY) {
    // Graduate straight out with the easy interval.
    return graduate(next, cfg.easyIntervalDays, nowMs, cfg);
  }
  if (grade === GRADE.AGAIN) {
    next.learning_step = 0;
    next.mastery_state = relearning ? MASTERY.LAPSED : MASTERY.LEARNING;
    next.due_at = isoAt(nowMs, steps[0] * MIN_MS);
    return next;
  }
  // HARD → repeat current step; GOOD → advance a step (graduating past the last).
  let step = s.learning_step ?? 0;
  if (grade === GRADE.GOOD) step += 1;
  if (step >= steps.length) {
    return graduate(next, cfg.graduatingIntervalDays, nowMs, cfg);
  }
  next.learning_step = step;
  next.mastery_state = relearning ? MASTERY.LAPSED : MASTERY.LEARNING;
  next.due_at = isoAt(nowMs, steps[step] * MIN_MS);
  return next;
}

function graduate(next, intervalDays, nowMs, cfg) {
  next.interval_days = roundInterval(intervalDays, cfg);
  next.repetitions = 1;
  next.learning_step = 0;
  next.mastery_state = reviewState(next.interval_days, cfg);
  next.due_at = isoAt(nowMs, next.interval_days * DAY_MS);
  return next;
}

// --- Review phase: day-intervals scaled by ease factor ----------------------
function stepReview(s, grade, nowMs, cfg) {
  const next = { ...s };

  if (grade === GRADE.AGAIN) {
    // A lapse: known item forgotten. Drop into relearning.
    next.lapse_count = (s.lapse_count ?? 0) + 1;
    next.repetitions = 0;
    next.interval_days = 0;
    next.learning_step = 0;
    next.ease_factor = clampEase(s.ease_factor - 0.2, cfg);
    next.mastery_state = MASTERY.LAPSED;
    next.due_at = isoAt(nowMs, cfg.relearnStepsMin[0] * MIN_MS);
    if (next.lapse_count >= cfg.leechLapses) {
      next.is_leech = true;
      if (cfg.suspendLeeches) next.is_suspended = true;
    }
    return next;
  }

  let interval;
  if (grade === GRADE.HARD) {
    interval = s.interval_days * cfg.hardMultiplier;
    next.ease_factor = clampEase(s.ease_factor - 0.15, cfg);
  } else if (grade === GRADE.EASY) {
    interval = s.interval_days * s.ease_factor * cfg.easyBonus;
    next.ease_factor = clampEase(s.ease_factor + 0.15, cfg);
  } else {
    // GOOD
    interval = s.interval_days * s.ease_factor;
    next.ease_factor = clampEase(s.ease_factor, cfg);
  }

  next.interval_days = roundInterval(interval, cfg);
  next.repetitions = (s.repetitions ?? 0) + 1;
  next.mastery_state = reviewState(next.interval_days, cfg);
  next.due_at = isoAt(nowMs, next.interval_days * DAY_MS);
  return next;
}

function isoAt(nowMs, offsetMs) {
  return new Date(nowMs + offsetMs).toISOString();
}

/**
 * Map an objective-game result to a self-grade (spec §8.1):
 * correct → Good, or Easy if answered quickly; incorrect → Again.
 */
export function gradeFromObjective(isCorrect, responseMs, fastThresholdMs = 3000) {
  if (!isCorrect) return GRADE.AGAIN;
  return responseMs != null && responseMs <= fastThresholdMs ? GRADE.EASY : GRADE.GOOD;
}
